import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AlertCircle, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { SecretInput } from '@/Domain/components/Shared/Input/SecretInput';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const DISCORD_INTEGRATION_NAME = 'discord' as const;
const DISCORD_QUERY_KEY = [QueryKey.integration.get, DISCORD_INTEGRATION_NAME] as const;

const DISCORD_ERROR_INVALID_TOKEN = 'InvalidToken';
const DISCORD_ERROR_PRIVILEGED_INTENTS = 'PrivilegedIntentsRequired';
const DISCORD_DOCS_URL = 'https://docs.quickchat.ai/channels/discord';
const DISCORD_DOCS_INTEGRATE_URL =
  'https://docs.quickchat.ai/channels/discord/#integrate-your-discord-bot-with-quickchat-ai';
const DISCORD_DOCS_CONFIGURE_URL =
  'https://docs.quickchat.ai/channels/discord/#configure-your-discord-application';
const DISCORD_SERVER_INVITE_URL = 'https://discord.gg/KqkHwvPRNH';
const LINK_CLASS =
  'font-medium text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground';
const TROUBLESHOOT_LINK_CLASS =
  'font-semibold underline underline-offset-2 transition-opacity hover:opacity-70';

// Poll the Discord status every 2s while a restart is in flight, and never
// keep the spinner up longer than 20s — matching the legacy modal's window.
const SYNC_POLL_INTERVAL_MS = 2_000;
const SYNC_TIMEOUT_MS = 20_000;

// Three-state connection status mirrored from the legacy Discord modal.
type DiscordConnectionState = 'connected' | 'disabled' | 'failed';
// Bot-restarting action shown as a spinner while we poll for fresh status.
type DiscordSyncAction = 'starting' | 'stopping' | 'updating_token' | null;
// Terminal outcome reported to PostHog when the post-save poll resolves.
type DiscordConnectStatus = 'connected' | 'failed' | 'disabled' | 'timeout';

export interface DiscordIntegrationParams {
  discord_bot_active: boolean;
  discord_bot_token: string | null;
  discord_bot_errors: string | null;
  discord_last_updated: string | null;
  discord_cross_channel_search_active: boolean;
  discord_respond_in_threads: boolean;
}

interface DiscordIntegrationResponse {
  name: 'discord';
  params: DiscordIntegrationParams;
}

const discordFormSchema = z.object({
  discord_bot_active: z.boolean(),
  discord_bot_token: z.string(),
  discord_respond_in_threads: z.boolean(),
  discord_cross_channel_search_active: z.boolean(),
});

type DiscordFormValues = z.infer<typeof discordFormSchema>;

const REPLY_MODE_THREADS = 'threads';
const REPLY_MODE_CHANNEL = 'channel';

export interface DiscordSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DiscordSheet = ({ open, onOpenChange }: DiscordSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  const [syncing, setSyncing] = useState(false);
  const [syncAction, setSyncAction] = useState<DiscordSyncAction>(null);
  const syncBaselineRef = useRef<number | null>(null);
  // Mirror syncAction into a ref so the hard-timeout callback (which is set up
  // once per sync cycle) can read the latest action without resetting its timer.
  const syncActionRef = useRef<DiscordSyncAction>(null);
  useEffect(() => {
    syncActionRef.current = syncAction;
  }, [syncAction]);

  const { data } = useQuery<DiscordIntegrationResponse>({
    queryKey: DISCORD_QUERY_KEY,
    enabled: open,
    refetchInterval: syncing ? SYNC_POLL_INTERVAL_MS : false,
  });

  const params = data?.params;

  const initialValues = useMemo<DiscordFormValues>(
    () => ({
      discord_bot_active: params?.discord_bot_active ?? false,
      discord_bot_token: params?.discord_bot_token ?? '',
      discord_respond_in_threads: params?.discord_respond_in_threads ?? false,
      discord_cross_channel_search_active: params?.discord_cross_channel_search_active ?? false,
    }),
    [params],
  );

  const form = useForm<DiscordFormValues>({
    resolver: zodResolver(discordFormSchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  // When the sheet (re)opens, reset transient sync state and record the open.
  useEffect(() => {
    if (open) {
      setSyncing(false);
      setSyncAction(null);
      capture(PostHogEventEnum.DiscordIntegrationSheetOpened);
    }
  }, [open, capture]);

  // Stop the post-save poll and report the outcome once Discord confirms a
  // fresh status (discord_last_updated advances past the baseline).
  useEffect(() => {
    if (!syncing) return;
    const updatedMs = params?.discord_last_updated ? Date.parse(params.discord_last_updated) : null;
    const advanced =
      syncBaselineRef.current === null
        ? updatedMs !== null
        : updatedMs !== null && updatedMs > syncBaselineRef.current;
    if (!advanced) return;
    const status: DiscordConnectStatus = params?.discord_bot_errors
      ? 'failed'
      : params?.discord_bot_active
        ? 'connected'
        : 'disabled';
    capture(PostHogEventEnum.DiscordIntegrationConnectResult, {
      status,
      reason: params?.discord_bot_errors || null,
      action: syncAction,
    });
    setSyncing(false);
    setSyncAction(null);
  }, [
    params?.discord_last_updated,
    params?.discord_bot_active,
    params?.discord_bot_errors,
    syncing,
    syncAction,
    capture,
  ]);

  // Hard timeout so the spinner is never stuck if Discord never reports back.
  // This must live in its own effect: React Query keeps the same data reference
  // when the polled status is unchanged, so the advance-detection effect above
  // would not re-run to observe the elapsed time.
  useEffect(() => {
    if (!syncing) return;
    const id = setTimeout(() => {
      capture(PostHogEventEnum.DiscordIntegrationConnectResult, {
        status: 'timeout',
        reason: null,
        action: syncActionRef.current,
      });
      setSyncing(false);
      setSyncAction(null);
    }, SYNC_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [syncing, capture]);

  const hasExistingToken = Boolean(params?.discord_bot_token);
  // Mirror the legacy modal's three-state status exactly: errors win, then
  // active = connected, otherwise disabled. No token check — an empty or invalid
  // token surfaces as a backend-reported error after the post-save poll.
  const connectionState: DiscordConnectionState | null = !params
    ? null
    : params.discord_bot_errors
      ? 'failed'
      : params.discord_bot_active
        ? 'connected'
        : 'disabled';

  const mutation = useMutation<
    DiscordIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof DISCORD_INTEGRATION_NAME; params: Partial<DiscordIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (responseData) => {
      // The bot status updates asynchronously via Discord's callback, so the PATCH
      // response status is stale (still "active", no error). Caching it lets a stale
      // "active" mask a later InvalidToken and show a false Connected — refetch the
      // real server state instead. The polling effect then converges on the truth.
      queryClient.invalidateQueries({ queryKey: DISCORD_QUERY_KEY });
      toast.success(t('integration:discord.saved'));
      form.reset({
        ...form.getValues(),
        discord_bot_token: responseData.params.discord_bot_token ?? '',
      });
    },
    onError: (error) => {
      // Surface the backend `detail` message when available; never include
      // the raw request body (which may carry the bot token).
      const detail = error.response?.data?.detail;
      capture(PostHogEventEnum.DiscordIntegrationSaveFailed, { reason: detail ?? 'unknown' });
      toast.error(detail ?? t('integration:discord.save_failed'));
    },
  });

  // Auto-save a single changed field immediately (no Save button). When the
  // change restarts the bot (enable toggle, or a token change while active),
  // show a spinner and poll until the Discord server reports fresh status —
  // mirroring the legacy modal's poll-until-confirmed flow.
  const persistParams = (
    partial: Partial<DiscordIntegrationParams>,
    syncActionForChange: DiscordSyncAction = null,
  ) => {
    if (Object.keys(partial).length === 0) return;
    // Show the preloader and start polling immediately (before the network
    // round-trip) so the toggle feels instant; revert if the save fails.
    if (syncActionForChange) {
      syncBaselineRef.current = params?.discord_last_updated
        ? Date.parse(params.discord_last_updated)
        : null;
      setSyncAction(syncActionForChange);
      setSyncing(true);
    }
    mutation.mutate(
      { name: DISCORD_INTEGRATION_NAME, params: partial },
      {
        onError: () => {
          if (syncActionForChange) {
            setSyncing(false);
            setSyncAction(null);
          }
        },
      },
    );
  };

  // Token auto-saves when the field loses focus; trim pasted whitespace so it
  // doesn't get stored verbatim and produce confusing 401s from Discord. The
  // field is prefilled with the saved token, so blurring it unchanged (or
  // cleared) is a no-op — the existing token is kept and the bot isn't restarted.
  const handleTokenBlur = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === (params?.discord_bot_token ?? '')) {
      return;
    }
    capture(PostHogEventEnum.DiscordIntegrationTokenSaved, {
      bot_active: Boolean(params?.discord_bot_active),
    });
    // Editing the token only restarts the bot when it's already running.
    persistParams(
      { discord_bot_token: trimmed },
      params?.discord_bot_active ? 'updating_token' : null,
    );
  };

  // Map known Discord error codes to actionable guidance; unknown errors fall back to generic help plus the raw detail.
  const renderErrorGuidance = (error: string) => {
    if (error === DISCORD_ERROR_INVALID_TOKEN) {
      return (
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.invalid_token"
          components={[
            <strong key="label" />,
            <a
              key="link"
              href={DISCORD_DOCS_INTEGRATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
          ]}
        />
      );
    }
    if (error === DISCORD_ERROR_PRIVILEGED_INTENTS) {
      return (
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.privileged_intents"
          components={[
            <strong key="label" />,
            <strong key="intent" />,
            <a
              key="link"
              href={DISCORD_DOCS_CONFIGURE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
          ]}
        />
      );
    }
    return (
      <>
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.generic"
          components={[
            <strong key="label" />,
            <a
              key="guide"
              href={DISCORD_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
            <a
              key="server"
              href={DISCORD_SERVER_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
          ]}
        />
        <span
          className="mt-2 block font-mono text-xs text-muted-foreground"
          data-testid="discord-error-message"
        >
          {error}
        </span>
      </>
    );
  };

  const renderStatus = () => {
    if (syncing) {
      return (
        <>
          <Badge
            variant="secondary"
            size="md"
            startIcon={<Loader2 className="size-3 animate-spin" />}
            data-testid="discord-status-syncing"
          >
            {t(`integration:discord.status.${syncAction ?? 'starting'}`)}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {t('integration:discord.status.sync_description')}
          </span>
        </>
      );
    }
    if (connectionState === 'connected') {
      return (
        <>
          <Badge
            variant="success"
            size="md"
            startIcon={<CheckCircle2 className="size-3" />}
            data-testid="discord-status-connected"
          >
            {t('integration:discord.status.connected')}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {t('integration:discord.status.connected_description')}
          </span>
        </>
      );
    }
    if (connectionState === 'failed') {
      return (
        <>
          <Badge
            variant="error"
            size="md"
            startIcon={<AlertCircle className="size-3" />}
            data-testid="discord-status-failed"
          >
            {t('integration:discord.status.failed')}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {t('integration:discord.status.failed_description')}
          </span>
        </>
      );
    }
    return (
      <>
        <Badge
          variant="secondary"
          size="md"
          startIcon={<Circle className="size-3" />}
          data-testid="discord-status-disabled"
        >
          {t('integration:discord.status.disabled')}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {t('integration:discord.status.disabled_description')}
        </span>
      </>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-3xl w-full h-full"
        tabIndex={undefined}
        data-testid="discord-sheet"
      >
        <SheetHeader className="px-0.5">
          <SheetTitle data-testid="discord-sheet-title">
            {t('integration:discord.title')}
          </SheetTitle>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="discord-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 space-y-6">
          <p className="text-sm text-muted-foreground" data-testid="discord-description">
            <Trans
              t={t}
              i18nKey="integration:discord.description"
              components={[
                <a
                  key="guide"
                  href={DISCORD_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASS}
                />,
              ]}
            />
          </p>

          {params && (
            <div className="flex items-center gap-2" data-testid="discord-status">
              {renderStatus()}
            </div>
          )}

          {params?.discord_bot_errors && !syncing && (
            <Alert
              variant="destructive"
              data-testid="discord-error-alert"
              className="border-destructive/15 shadow-sm"
            >
              <AlertCircle className="size-4" />
              <AlertTitle>{t('integration:discord.errors.heading')}</AlertTitle>
              <AlertDescription>{renderErrorGuidance(params.discord_bot_errors)}</AlertDescription>
            </Alert>
          )}

          <div
            className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border"
            data-testid="discord-form"
          >
            {/* Enable Discord */}
            <div className="flex items-center justify-between gap-4 p-4">
              <div className="flex-1">
                <Label
                  htmlFor="discord-enable-switch"
                  className="cursor-pointer text-base font-semibold"
                >
                  {t('integration:discord.enable')}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('integration:discord.hints.enable')}
                </p>
              </div>
              <Controller
                control={form.control}
                name="discord_bot_active"
                render={({ field }) => (
                  <Switch
                    id="discord-enable-switch"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      capture(PostHogEventEnum.DiscordIntegrationBotToggled, { enabled: checked });
                      persistParams(
                        { discord_bot_active: checked },
                        checked ? 'starting' : 'stopping',
                      );
                    }}
                    data-testid="discord-enable-switch"
                    className="scale-150"
                  />
                )}
              />
            </div>

            {/* Bot Token */}
            <div className="space-y-1.5 p-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="discord-bot-token-input">
                  {t('integration:discord.bot_token.label')}
                </Label>
                {hasExistingToken && (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium text-success"
                    data-testid="discord-bot-token-saved-badge"
                  >
                    <CheckCircle2 className="size-3.5" />
                    {t('integration:discord.bot_token.saved')}
                  </span>
                )}
              </div>
              <Controller
                control={form.control}
                name="discord_bot_token"
                render={({ field }) => (
                  <SecretInput
                    id="discord-bot-token-input"
                    placeholder={
                      hasExistingToken
                        ? t('integration:discord.bot_token.replace_placeholder')
                        : t('integration:discord.bot_token.placeholder')
                    }
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      field.onBlur();
                      handleTokenBlur(field.value);
                    }}
                    data-testid="discord-bot-token-input"
                    className="font-mono"
                  />
                )}
              />
              <p className="text-xs text-muted-foreground">
                {t('integration:discord.hints.token')}
              </p>
            </div>

            {/* Reply mode */}
            <div className="space-y-1.5 p-4">
              <Label htmlFor="discord-reply-mode">
                {t('integration:discord.reply_mode.label')}
              </Label>
              <Controller
                control={form.control}
                name="discord_respond_in_threads"
                render={({ field }) => (
                  <Select
                    value={field.value ? REPLY_MODE_THREADS : REPLY_MODE_CHANNEL}
                    onValueChange={(value) => {
                      const respondInThreads = value === REPLY_MODE_THREADS;
                      field.onChange(respondInThreads);
                      capture(PostHogEventEnum.DiscordIntegrationReplyModeChanged, {
                        respond_in_threads: respondInThreads,
                      });
                      persistParams({ discord_respond_in_threads: respondInThreads });
                    }}
                  >
                    <SelectTrigger
                      id="discord-reply-mode"
                      data-testid="discord-reply-mode-trigger"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        value={REPLY_MODE_CHANNEL}
                        data-testid="discord-reply-mode-channel"
                      >
                        {t('integration:discord.reply_mode.channel')}
                      </SelectItem>
                      <SelectItem
                        value={REPLY_MODE_THREADS}
                        data-testid="discord-reply-mode-threads"
                      >
                        {t('integration:discord.reply_mode.threads')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Cross-channel search */}
            <div className="flex items-start justify-between gap-4 p-4">
              <div className="flex-1">
                <Label htmlFor="discord-cross-channel-switch" className="cursor-pointer">
                  {t('integration:discord.cross_channel_search.label')}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('integration:discord.cross_channel_search.description')}
                </p>
              </div>
              <Controller
                control={form.control}
                name="discord_cross_channel_search_active"
                render={({ field }) => (
                  <Switch
                    id="discord-cross-channel-switch"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      capture(PostHogEventEnum.DiscordIntegrationCrossChannelToggled, {
                        enabled: checked,
                      });
                      persistParams({ discord_cross_channel_search_active: checked });
                    }}
                    data-testid="discord-cross-channel-switch"
                  />
                )}
              />
            </div>
          </div>

          <div className="space-y-2 border-t border-border pt-4 text-xs text-muted-foreground">
            <p data-testid="discord-footer-permissions">
              {t('integration:discord.footer.permissions')}
            </p>
            <p>
              <Trans
                t={t}
                i18nKey="integration:discord.footer.help"
                components={[
                  <a
                    key="server"
                    href={DISCORD_SERVER_INVITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_CLASS}
                  />,
                ]}
              />
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
